import type { ReactNode } from "react";
import {
  planDistanceM,
  type PlanEditStatus,
  type PlanPoint,
  type PlanRoom,
  type PlanSegment,
} from "@sts/geometry";
import { formatLength } from "@sts/scan-viewer";
import { toView, type PlanFit } from "./plan-view.js";
import { HIT_UNITS, midpoint, round, uprightAngle } from "./plan-marks.jsx";
import { editedByLine, recordFor } from "./plan-editor.js";
import type { HeldPlanEdit } from "./use-plan-edits.js";
import type { OpeningGhost, PlanEditor } from "./use-plan-editor.js";

/**
 * The plan editor's marks: which walls and openings were corrected (always
 * shown), and — with "Edit plan" on — the press targets that drag them, the
 * selected wall's end handles, and the wall or opening a tool would put down.
 *
 * Drawn over the plan's own walls so a press here is the editor's, not a pan
 * and not the plain selection underneath. Styling is CSS over the portal's
 * tokens (`.floor-plan__edit*` in styles.css); geometry and pointer behaviour
 * are inline, as in `plan-marks.tsx`.
 */

const INERT = { pointerEvents: "none" } as const;

/**
 * Every wall and opening on these rooms, once: a wall between two rooms of
 * one split capture is listed in both under one id (room-split.ts).
 */
function segmentsOnce(rooms: readonly PlanRoom[]): PlanSegment[] {
  const seen = new Set<string>();
  const out: PlanSegment[] = [];
  for (const room of rooms) {
    for (const segment of room.segments) {
      if (seen.has(segment.id)) continue;
      seen.add(segment.id);
      out.push(segment);
    }
  }
  return out;
}

/** A corrected element's marker: a small filled diamond beside its middle, with who and when. */
export function EditedMarks({
  rooms,
  fit,
  status,
  records,
}: {
  readonly rooms: readonly PlanRoom[];
  readonly fit: PlanFit;
  readonly status: ReadonlyMap<string, PlanEditStatus>;
  readonly records: readonly HeldPlanEdit[];
}): ReactNode {
  const marks: ReactNode[] = [];
  for (const segment of segmentsOnce(rooms)) {
    const state = status.get(segment.id);
    if (!state) continue;
    const kind = segment.kind === "wall" ? "wall" : "opening";
    const record = recordFor(records, kind, segment.id);
    const a = toView(segment.start, fit);
    const b = toView(segment.end, fit);
    const m = midpoint(a, b);
    const len = Math.hypot(b.x - a.x, b.y - a.y) || 1;
    // Off the line, so it never sits on a door swing or a window's glazing.
    const off = { x: (-(b.y - a.y) / len) * 9, y: ((b.x - a.x) / len) * 9 };
    const p = { x: m.x + off.x, y: m.y + off.y };
    marks.push(
      <g
        key={segment.id}
        className={`floor-plan__edited floor-plan__edited--${state}`}
        data-edited-for={segment.id}
        data-edit-state={state}
        style={INERT}
      >
        <title>{`${segment.label}: ${editedByLine(record) ?? (state === "added" ? "Added on the plan" : "Edited on the plan")}`}</title>
        <rect x={round(p.x - 3.5)} y={round(p.y - 3.5)} width={7} height={7} transform={`rotate(45,${round(p.x)},${round(p.y)})`} />
      </g>,
    );
  }
  return <g className="floor-plan__edited-layer" data-testid="plan-edited-layer">{marks}</g>;
}

/** With "Edit plan" on: press targets over every wall and opening, and the selected wall's handles. */
export function EditHandles({
  rooms,
  fit,
  editor,
  selectedId,
}: {
  readonly rooms: readonly PlanRoom[];
  readonly fit: PlanFit;
  readonly editor: PlanEditor;
  readonly selectedId: string | undefined;
}): ReactNode {
  const interactive = editor.tool === null;
  const segments = segmentsOnce(rooms);
  // Openings after walls, so an opening's target wins over the wall it is in.
  const ordered = [...segments.filter((s) => s.kind === "wall"), ...segments.filter((s) => s.kind !== "wall")];
  const selectedWall = segments.find((s) => s.id === selectedId && s.kind === "wall");
  return (
    <g className="floor-plan__edit-layer" data-testid="plan-edit-layer" style={interactive ? undefined : INERT}>
      {ordered.map((segment) => {
        const a = toView(segment.start, fit);
        const b = toView(segment.end, fit);
        const isWall = segment.kind === "wall";
        return (
          <line
            key={segment.id}
            className={isWall ? "floor-plan__edit-hit" : "floor-plan__edit-hit floor-plan__edit-hit--opening"}
            data-edit-hit={segment.id}
            x1={round(a.x)}
            y1={round(a.y)}
            x2={round(b.x)}
            y2={round(b.y)}
            stroke="transparent"
            strokeWidth={isWall ? HIT_UNITS : HIT_UNITS + 4}
            style={{ cursor: isWall ? "move" : "ew-resize" }}
            onPointerDown={(event) =>
              isWall ? editor.onWallPointerDown(segment.id, event) : editor.onOpeningPointerDown(segment.id, event)
            }
            // The press did the work; the click must not reach the plan and deselect.
            onClick={(event) => event.stopPropagation()}
          />
        );
      })}
      {selectedWall ? <WallHandles wall={selectedWall} fit={fit} editor={editor} /> : null}
    </g>
  );
}

function WallHandles({
  wall,
  fit,
  editor,
}: {
  readonly wall: PlanSegment;
  readonly fit: PlanFit;
  readonly editor: PlanEditor;
}): ReactNode {
  const ends = [
    { which: "start" as const, p: toView(wall.start, fit), letter: "A" },
    { which: "end" as const, p: toView(wall.end, fit), letter: "B" },
  ];
  return (
    <g className="floor-plan__edit-handles">
      {ends.map(({ which, p, letter }) => (
        <g
          key={which}
          className="floor-plan__edit-handle"
          data-wall-end={`${wall.id}:${which}`}
          style={{ cursor: "crosshair" }}
          onPointerDown={(event) => editor.onEndPointerDown(wall.id, which, event)}
          onClick={(event) => event.stopPropagation()}
        >
          <title>{`Drag to move end ${letter} of ${wall.label}`}</title>
          <circle cx={round(p.x)} cy={round(p.y)} r={9} />
          <text x={round(p.x)} y={round(p.y)} style={INERT}>
            {letter}
          </text>
        </g>
      ))}
    </g>
  );
}

/**
 * The middle of a-b, `px` off the line to one side — square to it, so a
 * label on an up-and-down wall sits beside the wall rather than on it.
 */
function offMiddle(
  a: { x: number; y: number },
  b: { x: number; y: number },
  px: number,
  toward?: { x: number; y: number },
): { x: number; y: number } {
  const m = midpoint(a, b);
  const len = Math.hypot(b.x - a.x, b.y - a.y);
  if (!(len > 1e-6)) return { x: m.x, y: m.y - px };
  let n = { x: (b.y - a.y) / len, y: -(b.x - a.x) / len };
  if (toward) {
    // Into the room (`toward` is its centre): the wall's own size is drawn outside.
    if (n.x * (toward.x - m.x) + n.y * (toward.y - m.y) < 0) n = { x: -n.x, y: -n.y };
  } else if (n.y > 0 || (n.y === 0 && n.x > 0)) {
    // Otherwise the side that is "up" on screen.
    n = { x: -n.x, y: -n.y };
  }
  return { x: m.x + n.x * px, y: m.y + n.y * px };
}

/** What an editor tool would put down: a new wall's rubber band, or an opening on the wall under the pointer. */
export function EditToolGhost({
  fit,
  rooms,
  pendingStart,
  hoverPoint,
  ghost,
}: {
  readonly fit: PlanFit;
  /** This storey's rooms: an opening's label goes into the room its wall bounds. */
  readonly rooms: readonly PlanRoom[];
  readonly pendingStart: PlanPoint | null;
  readonly hoverPoint: PlanPoint | null;
  readonly ghost: OpeningGhost | null;
}): ReactNode {
  if (ghost) {
    const a = toView(ghost.start, fit);
    const b = toView(ghost.end, fit);
    const room = rooms.find((r) => r.segments.some((seg) => seg.id === ghost.wall.id));
    const m = offMiddle(a, b, 18, room ? toView(room.centre, fit) : undefined);
    const angle = uprightAngle({ x: b.x - a.x, y: b.y - a.y });
    return (
      <g className={`floor-plan__edit-ghost floor-plan__edit-ghost--${ghost.kind}`} data-testid="plan-edit-ghost" style={INERT}>
        <line x1={round(a.x)} y1={round(a.y)} x2={round(b.x)} y2={round(b.y)} strokeWidth={10} />
        <text x={round(m.x)} y={round(m.y)} transform={`rotate(${round(angle)},${round(m.x)},${round(m.y)})`}>
          {`${ghost.kind === "door" ? "Door" : ghost.kind === "window" ? "Window" : "Opening"} ${formatLength(ghost.widthM, "m")}`}
        </text>
      </g>
    );
  }
  if (!pendingStart || !hoverPoint) return null;
  const a = toView(pendingStart, fit);
  const b = toView(hoverPoint, fit);
  const m = offMiddle(a, b, 14);
  const angle = uprightAngle({ x: b.x - a.x, y: b.y - a.y || 1e-9 });
  return (
    <g className="floor-plan__edit-ghost floor-plan__edit-ghost--wall" data-testid="plan-edit-ghost" style={INERT}>
      <line x1={round(a.x)} y1={round(a.y)} x2={round(b.x)} y2={round(b.y)} strokeWidth={6} strokeLinecap="square" />
      <text x={round(m.x)} y={round(m.y)} transform={`rotate(${round(angle)},${round(m.x)},${round(m.y)})`}>
        {formatLength(planDistanceM(pendingStart, hoverPoint), "m")}
      </text>
    </g>
  );
}
